import { useEffect, useRef, useState } from 'react'
import { Button, ErrorApi, Preloader, StickyFooter } from 'src/components'
import { getErrorString } from 'src/utils'
import { Organization } from '../../api'
import {
  useGetAdminOrganization,
  useEditAdminOrganization,
} from '../../queries'
import { useReferenceData } from '../hooks'
import Editor from './Editor'
import CustomButton from 'src/components/Shared/UIComponents/Button'
import { ValidOrg } from 'src/routes/Organization/Client/api'
import { toast } from 'sonner'

type PropsConfig = {
  organization: string
  content: ValidOrg
  username: string
}

function stringifyContent(content: Organization) {
  return JSON.stringify(content, null, 4)
}

function checkChanges(prev: string | undefined, next: Organization) {
  if (!prev || !next) return true
  try {
    const { edittimestamp: p, ...prevState } = JSON.parse(prev)
    const { edittimestamp: n, ...nextState } = next
    return JSON.stringify(prevState) !== JSON.stringify(nextState)
  } catch (_) {
    return true
  }
}

function OrganizationCode(props: PropsConfig) {
  const [state, setState] = useState(() => {
    const content = stringifyContent(props.content)
    return {
      initContent: content,
      content,
      hasServerChanges: false,
    }
  })

  const skippedServerChanges = useRef<string>()
  const editAdminOrg = useEditAdminOrganization()
  const contentRef = useReferenceData(state.content)

  const hasLocalChanges = state.content !== state.initContent

  useEffect(() => {
    if (
      !state.hasServerChanges &&
      checkChanges(state.initContent, props.content) &&
      checkChanges(skippedServerChanges.current, props.content)
    ) {
      const changes = checkChanges(contentRef.current, props.content)
      setState((state) => ({
        ...state,
        hasServerChanges: changes,
      }))
    }
  }, [props.content, state.hasServerChanges, state.initContent, contentRef])

  useEffect(() => {
    if (state.hasServerChanges) {
      if (hasLocalChanges) {
        toast.info(
          'The configuration of the selected organization has been updated. You can save your changes or resync the configuration',
        )
      } else {
        const content = stringifyContent(props.content)
        setState({
          content,
          initContent: content,
          hasServerChanges: false,
        })
      }
    }
  }, [state.hasServerChanges, hasLocalChanges, props.content])

  const resetChanges = () => {
    const content = stringifyContent(props.content)
    setState({
      content,
      initContent: content,
      hasServerChanges: false,
    })
  }

  const saveChanges = async () => {
    try {
      const changes = JSON.parse(state.content)
      await editAdminOrg.mutateAsync({ ...changes, username: props.username })
      skippedServerChanges.current = stringifyContent(props.content)
      setState((state) => ({
        ...state,
        initContent: state.content,
        hasServerChanges: false,
        canCheckServerChanges: false,
      }))
    } catch (error) {
      toast.error(getErrorString(error))
    }
  }

  return (
    <div>
      <Editor
        content={state.content}
        onChange={(value) =>
          setState((state) => ({ ...state, content: value }))
        }
      />

      {hasLocalChanges ? (
        <StickyFooter className="justify-center">
          <CustomButton
            label={state.hasServerChanges ? 'Resync changes' : 'Reset changes'}
            onClick={resetChanges}
            disabled={editAdminOrg.isLoading}
          />
          <Button
            label="Save changes"
            onClick={saveChanges}
            isLoading={editAdminOrg.isLoading}
            isPrimary
            className="ml-8"
          />
        </StickyFooter>
      ) : (
        <div className="w-full h-6" />
      )}
    </div>
  )
}

type Props = {
  organization: string
  username: string
}

export default function ValidateOrganization(props: Props) {
  const { data, status, error, refetch } = useGetAdminOrganization({
    organization: props.organization,
    username: props.username,
  })

  if (status === 'loading' || status === 'idle') {
    return <Preloader className="col-span-full" />
  }

  if (status === 'error') {
    return <ErrorApi message={error} onClick={refetch} />
  }

  return (
    <OrganizationCode
      key={props.organization}
      organization={props.organization}
      username={props.username}
      content={data}
    />
  )
}
